<template>
  <div v-if="showNotification" class="security-notification" :class="notificationType">
    <div class="notification-content">
      <div class="notification-icon">
        <i :class="iconClass" aria-hidden="true"></i>
      </div>
      <div class="notification-text">
        <h4 class="notification-title">{{ title }}</h4>
        <p class="notification-message">{{ message }}</p>
        <div v-if="showProgress" class="notification-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progress + '%' }"></div>
          </div>
          <span class="progress-text">{{ progress }}%</span>
        </div>
      </div>
      <button @click="dismiss" class="notification-close" aria-label="Close notification">
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['rate_limit', 'security', 'warning', 'info'].includes(value)
  },
  title: {
    type: String,
    required: true
  },
  message: {
    type: String,
    required: true
  },
  duration: {
    type: Number,
    default: 5000
  },
  showProgress: {
    type: Boolean,
    default: false
  }
})

const showNotification = ref(true)
const progress = ref(100)
let progressInterval = null

const notificationType = computed(() => {
  switch (props.type) {
    case 'rate_limit':
      return 'notification-rate-limit'
    case 'security':
      return 'notification-security'
    case 'warning':
      return 'notification-warning'
    case 'info':
      return 'notification-info'
    default:
      return 'notification-info'
  }
})

const iconClass = computed(() => {
  switch (props.type) {
    case 'rate_limit':
      return 'fas fa-clock'
    case 'security':
      return 'fas fa-shield-alt'
    case 'warning':
      return 'fas fa-exclamation-triangle'
    case 'info':
      return 'fas fa-info-circle'
    default:
      return 'fas fa-info-circle'
  }
})

const dismiss = () => {
  showNotification.value = false
  if (progressInterval) {
    clearInterval(progressInterval)
  }
}

const startProgress = () => {
  if (!props.showProgress) return

  const interval = 100 
  const totalSteps = props.duration / interval
  const stepSize = 100 / totalSteps

  progressInterval = setInterval(() => {
    progress.value -= stepSize
    if (progress.value <= 0) {
      dismiss()
    }
  }, interval)
}

onMounted(() => {
  if (props.duration > 0) {
    setTimeout(() => {
      dismiss()
    }, props.duration)

    if (props.showProgress) {
      startProgress()
    }
  }
})

onUnmounted(() => {
  if (progressInterval) {
    clearInterval(progressInterval)
  }
})
</script>

<style scoped>
.security-notification {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 1000;
  max-width: 400px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.notification-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
}

.notification-icon {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.notification-text {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  line-height: 1.2;
}

.notification-message {
  font-size: 0.75rem;
  margin: 0;
  line-height: 1.4;
  opacity: 0.9;
}

.notification-progress {
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.progress-bar {
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: currentColor;
  border-radius: 2px;
  transition: width 0.1s linear;
}

.progress-text {
  font-size: 0.625rem;
  font-weight: 500;
  min-width: 2rem;
  text-align: right;
}

.notification-close {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: background-color 0.2s ease;
}

.notification-close:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Rate Limit Notification */
.notification-rate-limit {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.notification-rate-limit .notification-icon {
  background: rgba(255, 255, 255, 0.2);
}

/* Security Notification */
.notification-security {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.notification-security .notification-icon {
  background: rgba(255, 255, 255, 0.2);
}

/* Warning Notification */
.notification-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.notification-warning .notification-icon {
  background: rgba(255, 255, 255, 0.2);
}

/* Info Notification */
.notification-info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.notification-info .notification-icon {
  background: rgba(255, 255, 255, 0.2);
}

/* Mobile Responsive */
@media (max-width: 640px) {
  .security-notification {
    top: 0.5rem;
    right: 0.5rem;
    left: 0.5rem;
    max-width: none;
  }

  .notification-content {
    padding: 0.75rem;
  }

  .notification-title {
    font-size: 0.8125rem;
  }

  .notification-message {
    font-size: 0.6875rem;
  }
}
</style>
